import React, { useState, useEffect } from 'react';
import { MapPin, Search, RotateCcw, Trash2, Archive } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import AdminLayout from '../../components/admin/AdminLayout';
import ConfirmModal from '../../components/ConfirmModal';
import { adminApi } from '../../lib/api';

import { toast } from 'sonner';

const ArchivedLocations = () => {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [restoreModal, setRestoreModal] = useState({ isOpen: false, locationId: null, locationName: '' });
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, locationId: null, locationName: '' });
  const [actionLoading, setActionLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    fetchArchivedLocations();
  }, []);

  const fetchArchivedLocations = async () => {
    try {
      setLoading(true);
      const response = await adminApi.get(`/admin/locations/archived/list`);
      setLocations(response.data.locations);
    } catch (err) {
      console.error('Error fetching archived locations:', err);
      toast.error('Failed to load archived hubs');
    } finally {
      setLoading(false);
    }
  };

  const openRestoreModal = (location) => {
    setRestoreModal({ isOpen: true, locationId: location.id, locationName: location.name });
    setErrorMessage('');
  };

  const closeRestoreModal = () => {
    setRestoreModal({ isOpen: false, locationId: null, locationName: '' });
    setErrorMessage('');
  };

  const handleRestore = async () => {
    setActionLoading(true);
    setErrorMessage('');
    
    try {
      await adminApi.patch(
        `/admin/locations/${restoreModal.locationId}/archive?archived=false`,
        {}
      );
      toast.success('Hub restored successfully!');
      closeRestoreModal();
      fetchArchivedLocations();
    } catch (err) {
      const d = err.response?.data?.detail;
      setErrorMessage(typeof d === 'string' ? d : d?.message || 'Failed to restore hub');
    } finally {
      setActionLoading(false);
    }
  };

  const openDeleteModal = (location) => {
    setDeleteModal({ isOpen: true, locationId: location.id, locationName: location.name });
    setErrorMessage('');
  };

  const closeDeleteModal = () => {
    setDeleteModal({ isOpen: false, locationId: null, locationName: '' });
    setErrorMessage('');
  };

  const handleDelete = async () => {
    setActionLoading(true);
    setErrorMessage('');
    
    try {
      await adminApi.delete(`/admin/locations/${deleteModal.locationId}`);
      toast.success('Hub permanently deleted');
      closeDeleteModal();
      fetchArchivedLocations();
    } catch (err) {
      const d = err.response?.data?.detail;
      setErrorMessage(typeof d === 'string' ? d : d?.message || 'Failed to delete hub');
    } finally {
      setActionLoading(false);
    }
  };

  const filteredLocations = locations.filter(l =>
    l.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  // Loading state
  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-venue border-t-transparent"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      {/* V5 Page Header */}
      <div className="mb-8">
        <p className="font-mono text-venue text-sm mb-1">// archived_hubs</p>
        <h1 className="font-display text-3xl text-navy">Archived Hubs</h1>
        <p className="text-navy/50 mt-1">
          Restore or permanently delete archived hub locations • {locations.length} archived
        </p>
      </div>

      {/* V5 Search Card */}
      <div className="bg-white rounded-2xl p-5 mb-6 border border-navy/5 shadow-sm" style={{ borderLeft: '4px solid #10B981' }}>
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="flex-1 relative">
            <Search size={18} className="absolute left-4 top-1/2 transform -translate-y-1/2 text-navy/30" />
            <Input
              placeholder="Search by hub name..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-11 h-12 border-2 border-navy/10 focus:border-venue rounded-xl"
            />
          </div>
          <Button 
            onClick={fetchArchivedLocations}
            className="h-12 px-6"
            style={{ background: 'linear-gradient(135deg, #10B981, #059669)', color: 'white' }}
          >
            <Search size={18} className="mr-2" />
            Search
          </Button>
        </div>
      </div>

      {/* V5 Results Card */}
      <div className="bg-white rounded-2xl border border-navy/5 shadow-sm overflow-hidden">
        {filteredLocations.length === 0 ? (
          <div className="p-12 text-center">
            <div className="w-20 h-20 bg-navy/5 rounded-full flex items-center justify-center mx-auto mb-6">
              <Archive className="w-10 h-10 text-navy/20" />
            </div>
            <h3 className="font-display text-xl text-navy mb-2">No Archived Hubs</h3>
            <p className="text-navy/50">
              {searchQuery ? 'No archived hubs match your search' : 'No hubs have been archived'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-navy/5 bg-cream/50">
                  <th className="px-6 py-4 text-left text-xs font-semibold text-navy/50 uppercase tracking-wide">Hub</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-navy/50 uppercase tracking-wide">Location</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-navy/50 uppercase tracking-wide">Businesses</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-navy/50 uppercase tracking-wide">Status</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-navy/50 uppercase tracking-wide">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-navy/5">
                {filteredLocations.map((location) => (
                  <tr key={location.id} className="hover:bg-cream/30 transition-colors">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-venue to-venue/70 flex items-center justify-center text-white">
                          <MapPin size={18} />
                        </div>
                        <div>
                          <p className="font-semibold text-navy">{location.name}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-navy/70">
                      {location.address?.city || location.city}, {location.address?.state || location.state}
                    </td>
                    <td className="px-6 py-4">
                      <span className="px-2.5 py-1 bg-venue/10 text-venue text-sm font-medium rounded-full">
                        {location.business_count || 0}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="px-2.5 py-1 bg-amber-100 text-amber-700 text-sm font-medium rounded-full">
                        Archived
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <Button
                          onClick={() => openRestoreModal(location)}
                          size="sm"
                          className="rounded-xl"
                          style={{ background: 'linear-gradient(135deg, #22c55e, #16a34a)', color: 'white' }}
                        >
                          <RotateCcw size={14} className="mr-1.5" />
                          Restore
                        </Button>
                        <Button
                          onClick={() => openDeleteModal(location)}
                          size="sm"
                          variant="outline"
                          className="rounded-xl border-red-200 text-red-600 hover:bg-red-50"
                        >
                          <Trash2 size={14} />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Restore Modal */}
      <ConfirmModal
        isOpen={restoreModal.isOpen}
        onClose={closeRestoreModal}
        onConfirm={handleRestore}
        title="Restore Hub"
        message={
          <div>
            <p className="mb-2">Restore <strong>{restoreModal.locationName}</strong> to active hubs?</p>
            <p className="text-sm text-navy/50">The hub will be visible again and businesses can be added to it.</p>
            {errorMessage && (
              <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-800">{errorMessage}</p>
              </div>
            )}
          </div>
        }
        confirmText="Restore"
        cancelText="Cancel"
        danger={false}
        loading={actionLoading}
      />

      {/* Delete Modal */}
      <ConfirmModal
        isOpen={deleteModal.isOpen}
        onClose={closeDeleteModal}
        onConfirm={handleDelete}
        title="Permanently Delete Hub"
        message={
          <div>
            <p className="mb-2">Permanently delete <strong>{deleteModal.locationName}</strong>?</p>
            <p className="text-sm text-red-600 font-medium">This action cannot be undone! All associated data will be lost.</p>
            {errorMessage && (
              <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-800">{errorMessage}</p>
              </div>
            )}
          </div>
        }
        confirmText="Delete Permanently"
        cancelText="Cancel"
        danger={true}
        loading={actionLoading}
      />
    </AdminLayout>
  );
};

export default ArchivedLocations;
